// NOMBASE - Floating Pill Sidebar with Combined Messages/Notifications Drawer
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { prefetch } from '@/lib/queryClient';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import AppLauncher from './AppLauncher';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Menu, LayoutDashboard, 
  FileText,
  CheckCircle, Settings,
  Boxes,
  ShoppingBag,
  Store,
  BarChart3,
  Search,
  Building2,
  FlaskConical,
  Package,
  Factory,
  FolderOpen,
  LogOut,
  Gauge,
  Briefcase,
  CalendarRange,
  Send,
  Wrench,
  Workflow,
  GitBranch,
  Truck,
  SlidersHorizontal,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import CmdK from '@/components/primitives/CmdK';
import { useSidebarConfig } from '@/hooks/useSidebarConfig';
import { useApiQuery } from '@/hooks/useApiQuery';
import { getIcon } from '@/lib/iconRegistry';
import { toast } from 'sonner';

// Brand + supplier nav data lives in a pure module so the regression
// suite can import it without booting react-router-dom / Clerk. We
// re-export here so existing callers (`import { brandNavItems } from
// '@/components/layout/AppShell'`) keep working unchanged.
export { brandNavItems, getSupplierNavItems } from '@/lib/sidebarNav';
import { brandNavItems, getSupplierNavItems } from '@/lib/sidebarNav';
import { DEMAND_ENABLED, AUTOPILOT_ENABLED } from '@/lib/featureFlags';
import AutopilotToggle from '@/components/autopilot/AutopilotToggle';
import SystemAdminLink from '@/components/SystemAdminLink';

// System-admin nav was removed — the Admin Portal is a separate React
// app. Brand and supplier nav definitions below are the only roles this
// shell renders.



// Legacy: default supplier nav items for backward compat
const supplierNavItems = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  { label: 'Requests', href: '/requests', icon: FileText },
  { label: 'Ingredients', href: '/ingredients', icon: Boxes },
  { label: 'Orders', href: '/orders', icon: ShoppingBag },
  { label: 'Analytics', href: '/analytics', icon: BarChart3 },
  { label: 'Storefront', href: '/storefront', icon: Store },
];

function NavItem({ item, unreadCount = 0, draggable = false, index, onDragStart, onDragOver, onDrop, onRemove, dragState }) {
  const location = useLocation();
  const IconResolved = item.icon || getIcon(item.iconName);
  // Active matching: exact match for "/" (home), prefix-match-with-boundary
  // for everything else so sub-routes like /operations/today still light
  // up the parent /operations icon. Boundary check (`==='/' or '?''`)
  // prevents false positives like /productX matching /product.
  const path = location.pathname;
  const href = item.href;
  const isActive =
    href === '/'
      ? path === '/'
      : path === href || path.startsWith(href + '/');
  // Show an unread badge for any nav item that's been wired to one.
  // The "Messages" / "Operations" pages set this; everything else
  // explicitly receives 0 from the parent.
  const showBadge = unreadCount > 0;
  const isDragOverBefore = dragState?.overIndex === index && dragState?.overPosition === 'before';
  const isDragOverAfter = dragState?.overIndex === index && dragState?.overPosition === 'after';
  const isBeingDragged = typeof index === 'number' && dragState?.fromIndex === index;

  // Hover-prefetch: warm up the react-query cache for the likely next page
  // so the route feels instant once clicked. We only prefetch known-safe
  // read endpoints. The map is intentionally small and can grow over time.
  const handleHover = useCallback(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const headers = { 'Content-Type': 'application/json' };
    const fetchPath = async (path) => {
      const r = await fetch(`${API}${path}`, { credentials: 'include', headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    };
    const PREFETCH_MAP = {
      '/': ['/api/dashboard/brand'],
      '/network': ['/api/tenants/suppliers'],
      '/products': ['/api/products?limit=50'],
      '/development': ['/api/development/mrp-formulas'],
      '/procurement': ['/api/purchase-orders'],
      '/plan': ['/api/plan'],
      '/production': ['/api/production-runs'],
      '/ingredients': ['/api/ai-extract/sessions?status=draft'],
      '/flavors': ['/api/ai-extract/sessions?status=draft'],
      '/packaging': ['/api/ai-extract/sessions?status=draft'],
      '/services': ['/api/ai-extract/sessions?status=draft'],
    };
    const paths = PREFETCH_MAP[item.href];
    if (paths) {
      paths.forEach((p) => prefetch(['api', p], () => fetchPath(p)));
    }
  }, [item.href]);

  // Compute whether the current drag hovers above/below the midpoint so we
  // can show an insertion bar in the right spot. Purely a UI affordance —
  // the onDrop calculation re-reads the position to stay in sync.
  const handleDragOver = useCallback((e) => {
    // Accept drops from both sidebar-reorder AND launcher-add. Using
    // 'copy' is universally accepted (effectAllowed 'copyMove' on source).
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position = e.clientY < midpoint ? 'before' : 'after';
    onDragOver?.(index, position);
  }, [index, onDragOver]);

  const wrapperInner = (
    <Link
      to={item.href}
      onMouseEnter={handleHover}
      onFocus={handleHover}
      draggable={false}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'relative flex flex-col items-center justify-center gap-1 w-14 py-1.5 rounded-lg transition-all duration-200',
        isBeingDragged && 'opacity-40'
      )}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Active state paints only the icon chip — not the label — so the
          rail reads as an icon tile with a caption beneath it (reference
          rail language), instead of one solid ink block over both. The
          hover highlight is driven by the wrapper's `group` so hovering
          anywhere on the item (icon or label) lights the chip. */}
      <span
        className={cn(
          'flex items-center justify-center w-11 h-11 rounded-2xl transition-all duration-200',
          isActive
            ? 'bg-primary text-white shadow-sm'
            : 'text-muted-foreground group-hover:bg-secondary group-hover:text-foreground'
        )}
      >
        <IconResolved className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
      </span>
      <span
        className={cn(
          'text-[9px] leading-none transition-colors duration-200',
          isActive
            ? 'font-semibold text-foreground'
            : 'font-medium text-muted-foreground group-hover:text-foreground'
        )}
      >
        {item.label}
      </span>
      {showBadge && (
        <span className="absolute top-0.5 right-1.5 min-w-[18px] h-[18px] px-1 bg-accent text-white text-[10px] rounded-full flex items-center justify-center font-semibold shadow-sm z-20">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
      {draggable && onRemove && !item.core && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(item.id); }}
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gray-900 text-white text-[10px] leading-none items-center justify-center shadow opacity-0 group-hover:flex hidden"
          title={`Remove ${item.label}`}
          aria-label={`Remove ${item.label} from sidebar`}
          data-testid={`sidebar-remove-${item.id}`}
        >
          ×
        </button>
      )}
    </Link>
  );

  return (
    <div
      className={cn(
        'relative group',
        // Insertion markers — thin bars above/below the item when hovered
        isDragOverBefore && 'before:absolute before:-top-1 before:left-1 before:right-1 before:h-0.5 before:bg-sky-400 before:rounded-full',
        isDragOverAfter && 'after:absolute after:-bottom-1 after:left-1 after:right-1 after:h-0.5 after:bg-sky-400 after:rounded-full'
      )}
      draggable={draggable || undefined}
      onDragStart={draggable ? (e) => {
        // 'copyMove' so both the modal card (move/unpin) and sidebar
        // reorder (move) drop targets accept without effect mismatch.
        e.dataTransfer.effectAllowed = 'copyMove';
        e.dataTransfer.setData('application/x-nombase-sidebar', JSON.stringify({
          source: 'sidebar', index,
          id: item.id, label: item.label, href: item.href, iconName: item.iconName,
        }));
        // Use the icon button (inside the Link) as the drag ghost so the
        // user sees a clean 44×44 icon floating with the cursor instead of
        // the browser's default link URL preview.
        try {
          const iconEl = e.currentTarget.querySelector('a');
          if (iconEl) {
            const rect = iconEl.getBoundingClientRect();
            e.dataTransfer.setDragImage(iconEl, rect.width / 2, rect.height / 2);
          }
        } catch { /* noop */ }
        onDragStart?.(index);
      } : undefined}
      onDragOver={draggable ? handleDragOver : undefined}
      onDrop={draggable ? (e) => {
        e.preventDefault();
        // Stop bubbling to the parent <nav> — otherwise its catch-all
        // `onEndDrop` fires second and reorders to the end, undoing the
        // precise insertion we just computed.
        e.stopPropagation();
        onDrop?.(e, index);
      } : undefined}
      data-testid={draggable ? `sidebar-drag-${item.id}` : undefined}
    >
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{wrapperInner}</TooltipTrigger>
        <TooltipContent
          side="right"
          className="rounded-lg px-3 py-1.5 text-sm font-medium bg-navy-900 text-white border-0 shadow-md"
          sideOffset={12}
        >
          {item.label} {showBadge && `(${unreadCount})`}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function FloatingSidebar({ navItems, isBrand }) {
  // No per-nav unread badges in the brand-only MVP — the Operations Inbox
  // that drove the only badge was removed.
  const unreadById = useMemo(() => ({}), []);
  const { currentUser, currentTenant, tenantType, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const isSearchActive = location.pathname === '/requests/marketplace';

  // Split fixed Home from the customizable list. Home (and its supplier
  // cousin) is always pinned; everything else the user can reorder or
  // remove, and new items can be dragged in from the app launcher.
  const fixedTopItem = navItems.find(i => i.fixed) || navItems[0];
  const defaultCustomItems = useMemo(
    () => navItems.filter(i => i.id !== fixedTopItem?.id).map(i => ({
      id: i.id, label: i.label, href: i.href, iconName: i.iconName,
      // Preserve `core` so useSidebarConfig can derive its coreIds
      // set and gate removal accordingly.
      ...(i.core ? { core: true } : {}),
    })),
    [navItems, fixedTopItem?.id]
  );

  // Storage-key role: brand-only MVP — always 'brand'. Kept as a
  // variable rather than a literal so the per-user sidebar-layout
  // cache key stays parameterized in case we ever re-introduce
  // multi-persona.
  const role = 'brand';

  // Fetch the tenant-wide default so new seats inherit and admins can
  // manage it via the launcher. Cached in react-query so the launcher
  // and sidebar read from the same source.
  const { data: teamDefaultData } = useApiQuery('/api/tenants/me/sidebar-default', {
    key: ['tenant-sidebar-default'],
    staleTime: 5 * 60 * 1000,
  });
  const teamDefaultItems = teamDefaultData?.items;

  const { items, reorder, add, remove, reset, applyConfig } = useSidebarConfig(
    role,
    tenantType || 'x',
    defaultCustomItems,
    teamDefaultItems,
  );

  const isTenantAdmin = currentUser?.role === 'admin';

  // Drag state — purely UI, re-derived on every drag; nothing persisted.
  const [dragState, setDragState] = useState({ fromIndex: null, overIndex: null, overPosition: null });

  const onDragStart = useCallback((index) => {
    setDragState((s) => ({ ...s, fromIndex: index }));
  }, []);

  const onDragOver = useCallback((index, position) => {
    setDragState((s) => (
      s.overIndex === index && s.overPosition === position ? s : { ...s, overIndex: index, overPosition: position }
    ));
  }, []);

  const onDrop = useCallback((e, targetIndex) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/x-nombase-sidebar');
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      const position = dragState.overPosition || 'before';
      const insertAt = position === 'after' ? targetIndex + 1 : targetIndex;
      if (data.source === 'sidebar') {
        // Reorder within the sidebar. Account for the pull-out of the
        // source index when computing the final insertion point.
        const from = data.index;
        const to = from < insertAt ? insertAt - 1 : insertAt;
        reorder(from, to);
      } else if (data.source === 'launcher') {
        if (items.some(x => x.id === data.id)) {
          toast.info(`${data.label} is already in the sidebar`);
        } else if (items.length >= defaultCustomItems.length) {
          toast.error(`Sidebar is full (max ${defaultCustomItems.length})`);
        } else {
          add({ id: data.id, label: data.label, href: data.href, iconName: data.iconName }, insertAt);
          toast.success(`Added ${data.label} to sidebar`);
        }
      }
    } catch { /* ignore malformed drag */ }
    setDragState({ fromIndex: null, overIndex: null, overPosition: null });
  }, [dragState.overPosition, reorder, add, items, defaultCustomItems.length]);

  // Drop handler on the empty trailing space of the nav container — lets
  // users drop a launcher tile when the sidebar is empty or when they
  // aim past the last item.
  const onEndDrop = useCallback((e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/x-nombase-sidebar');
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (data.source === 'launcher' && !items.some(x => x.id === data.id)) {
        if (items.length >= defaultCustomItems.length) {
          toast.error(`Sidebar is full (max ${defaultCustomItems.length})`);
        } else {
          add({ id: data.id, label: data.label, href: data.href, iconName: data.iconName });
          toast.success(`Added ${data.label} to sidebar`);
        }
      } else if (data.source === 'sidebar') {
        // Reorder to the end
        reorder(data.index, items.length - 1);
      }
    } catch { /* noop */ }
    setDragState({ fromIndex: null, overIndex: null, overPosition: null });
  }, [items, defaultCustomItems.length, add, reorder]);

  function getInitials(name) {
    if (!name) return '';
    return name.split(' ').map(function(n) { return n[0]; }).join('');
  }

  function handleLogout() {
    // `logout()` (from AuthContext) is async and ends with
    // `navigateToSignIn('sign_out')`, which `window.location.assign`s
    // to the Gateway's `/sign-out?return_to=<gw>` so Clerk drops its
    // session too. Do NOT also `navigate('/login')` here — that's a
    // synchronous in-SPA route change that races with (and wins
    // over) the async Gateway redirect, landing the user on a
    // non-existent MRP `/login` route that just renders the unauth
    // card. Trust `logout()` to handle the navigation.
    logout();
  }

  // ── second handleLogout (mobile header sibling) is identical —
  //    see canonical comment above the desktop variant.

  // 2026-06-02 — Publish the sidebar's effective left-edge reserve
  // (its width + the 16px left offset + a small breathing gap) to a
  // CSS variable on <html>. AppShell's <main> consumes this via
  // `lg:pl-[var(--sidebar-reserve,0px)]` — when the FloatingSidebar
  // unmounts (e.g. user toggled it off while the AI drawer is open),
  // the variable disappears and the page reclaims that left padding
  // automatically. Mirrors the `--ai-drawer-width` mechanism on the
  // right edge so both rails participate in the same layout protocol.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--sidebar-reserve', '7rem'); // 112px ≈ pl-28
    return () => {
      root.style.removeProperty('--sidebar-reserve');
    };
  }, []);

  return (
    <TooltipProvider>
      {/* bottom-20 keeps the rail clear of the fixed bottom-left cluster
          (AI chat launcher + help) that lives in the page corner. */}
      <aside className="hidden lg:flex flex-col items-center fixed left-3 top-0 bottom-20 pt-5 z-50">
        {/* Brand mark above the rail — wordmark + CONNECT lettering,
            matching the reference rail header. */}
        <Link
          to="/"
          className="mb-3 flex flex-col items-center gap-1"
          aria-label="Nombase Connect — home"
        >
          <img
            src="/nombase-logo-tight.png"
            alt="nombase"
            className="w-16 h-auto select-none"
            draggable={false}
          />
          <span className="text-[7px] font-bold tracking-[2.5px] text-foreground leading-none">
            CONNECT
          </span>
        </Link>
        <div className="flex flex-col items-center flex-1 min-h-0 bg-white rounded-[20px] shadow-md py-3 px-2 transition-colors duration-200">
          {/* Pinned top: Dashboard/Home — never movable, never removable */}
          {fixedTopItem && (
            <NavItem
              key={fixedTopItem.id}
              item={fixedTopItem}
              unreadCount={unreadById[fixedTopItem.id] || 0}
            />
          )}

          {/* Customizable middle list (drag to reorder; drop from launcher to add) */}
          <nav
            className="flex flex-col items-center gap-0.5 mt-0.5 flex-1 min-h-0 overflow-y-auto scrollbar-hide"
            // HTML5 DnD requires preventDefault on BOTH dragenter and
            // dragover for the element (and children) to accept a drop.
            // We preventDefault unconditionally and set dropEffect='copy'
            // to match the launcher's effectAllowed='copy' (browsers
            // reject drops when the effects don't intersect).
            onDragEnter={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={onEndDrop}
            data-testid="sidebar-custom-nav"
          >
            {items.map((item, idx) => (
              <NavItem
                key={item.id}
                item={item}
                index={idx}
                draggable
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onRemove={remove}
                dragState={dragState}
                unreadCount={unreadById[item.id] || 0}
              />
            ))}
            {/* Empty placeholder so users can drop into an empty sidebar */}
            {items.length === 0 && (
              <div className="w-14 h-12 rounded-lg border border-dashed border-slate-200 text-[9px] text-slate-400 flex items-center justify-center text-center px-1 leading-tight">
                Drag here
              </div>
            )}
            {/* System Admin handoff — renders ONLY for allowlisted
                Clerk user ids. Internal devtools-style entry; lives in
                the nav cluster so it shares spacing with the regular
                items. UI gate only — the Gateway is the authoritative
                allowlist. See /app/memory/MRP_CONNECTION_SPEC.md. */}
            <SystemAdminLink />
          </nav>

          {/* Divider between the nav cluster and the user profile —
              a soft hairline that fades at both ends so it reads as a
              separator without a hard edge on the rounded rail. */}
          <div
            className="my-3 h-px w-7 bg-gradient-to-r from-transparent via-gray-300/80 to-transparent"
            role="separator"
            aria-hidden="true"
          />

          {/* Apps Launcher hidden — sidebar is now a fixed, curated set. */}

          {/* Settings moved into the user-profile dropdown below to free
              up vertical space on the rail and put account-management
              items in one consistent place. */}

          {/* User Profile */}
          <DropdownMenu>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center justify-center"
                    aria-label="Open account menu"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary text-white">
                      <span className="text-xs font-bold">{getInitials(currentUser?.name)}</span>
                    </div>
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent 
                side="right" 
                className="rounded-lg px-3 py-1.5 text-sm font-medium bg-navy-900 text-white border-0 shadow-md"
                sideOffset={12}
              >
                <p className="text-sm font-medium">{currentUser?.name}</p>
                <p className="text-xs text-gray-400">{currentTenant?.name}</p>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent 
              align="start" 
              side="right" 
              className="w-64 rounded-3xl p-2 shadow-xl border-0"
              sideOffset={16}
            >
              <div className="px-3 py-3 mb-1">
                <p className="font-semibold">{currentUser?.name}</p>
                <p className="text-xs text-gray-400">{currentUser?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-gray-100" />
              {/* Co-manufacturer capability-profile link — REMOVED
                  (Feb 2026 brand-only MVP excision). */}
              {/* Settings — visible to every role. Brand users land on
                  the company-settings page; suppliers/co-mfgs land on
                  the same role-aware destination. Previously this lived
                  on the sidebar rail (above the avatar); moved here so
                  the rail stays focused on primary nav. */}
              <Link to="/setup/company">
                <DropdownMenuItem
                  className="rounded-2xl mx-1 cursor-pointer py-2.5"
                  data-testid="nav-settings"
                >
                  <Settings className="w-4 h-4 mr-3 text-gray-500" />
                  <span>Settings</span>
                </DropdownMenuItem>
              </Link>
              {/* Supplier "Preferences" link (formerly gated on !isBrand)
                  — REMOVED (Feb 2026 brand-only MVP excision). */}
              <DropdownMenuSeparator className="bg-gray-100" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="rounded-2xl mx-1 cursor-pointer py-2.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-3" />
                <span>Log Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Autopilot toggle — its own control below the rail, under the profile
            icon; mirrors the NotificationBell above. Not a nav item. */}
        <div className="mt-3">
          <AutopilotToggle />
        </div>
      </aside>
    </TooltipProvider>
  );
}

// Mobile Header
function MobileHeader({ isBrand }) {
  const { currentUser, tenantType, logout } = useApp();
  const navigate = useNavigate();
  const mobilePath = useLocation().pathname;
  // Same effective-tenant rule as the desktop shell — never recompute
  // from raw Clerk metadata here, or we re-introduce the MVP gating
  // bug for brand tenants with missing Clerk org publicMetadata.
  const navItems = (isBrand ? brandNavItems : getSupplierNavItems(tenantType))
    .filter((i) => i.id !== 'demand' || DEMAND_ENABLED)
    .filter((i) => i.id !== 'autopilot' || AUTOPILOT_ENABLED);

  function getInitials(name) {
    if (!name) return '';
    return name.split(' ').map(function(n) { return n[0]; }).join('');
  }

  function handleLogout() {
    // See canonical comment above the desktop `handleLogout` — same
    // reasoning: `logout()` itself triggers the Gateway sign-out
    // redirect via `window.location.assign`; an additional SPA
    // `navigate('/login')` would race the async redirect and win.
    logout();
  }

  return (
    <header className="lg:hidden flex items-center justify-between px-4 py-2 bg-white border-b border-gray-100 sticky top-0 z-50">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-xl" aria-label="Open navigation menu">
            <Menu className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 p-0" aria-describedby={undefined}>
          <span className="sr-only">Navigation menu</span>
          {/* Logo in sidebar */}
          <div className="p-4 border-b border-gray-100">
            <div className="block">
              <img 
                src="/nombase-logo.png" 
                alt="NOMBASE" 
                className="h-8 w-auto"
              />
            </div>
          </div>
          
          {/* Navigation — same active treatment as the desktop rail
              (ink pill on the current section). */}
          <nav className="p-4 space-y-1">
            {navItems.map(function(item) {
              const Icon = item.icon;
              const isActive =
                item.href === '/'
                  ? mobilePath === '/'
                  : mobilePath === item.href || mobilePath.startsWith(item.href + '/');
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl transition-colors',
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2 : 1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
          
          {/* User Info & Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <span className="text-white font-semibold text-sm">{getInitials(currentUser?.name)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-gray-900 truncate">{currentUser?.name}</p>
                <p className="text-xs text-gray-500 truncate">{currentUser?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Settings — every role can reach Settings from here.
                  Suppliers/co-mfgs additionally get a Preferences shortcut. */}
              <Link to="/setup/company" className="flex-1">
                <Button variant="outline" size="sm" className="w-full rounded-xl">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </Link>
              {!isBrand && (
                <Link to="/settings/supplier-preferences">
                  <Button variant="outline" size="sm" className="rounded-xl" aria-label="Preferences">
                    <SlidersHorizontal className="w-4 h-4" />
                  </Button>
                </Link>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                aria-label="Log out"
                className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Center Logo */}
      <div className="block">
        <img 
          src="/nombase-logo.png" 
          alt="NOMBASE" 
          className="h-7 w-auto"
        />
      </div>


    </header>
  );
}

function AppShell({ children }) {
  const { isBrand, tenantType, currentTenant, currentUser } = useApp();
  // 2026-06-03 — auto-collapse the floating sidebar while the
  // left-anchored document preview modal (PdfSourceModal) is open; it
  // overlaps the sidebar pill on the left edge. PdfSourceModal dispatches
  // `nombase:document-modal` {open} on mount/close (last-event-wins).
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  useEffect(() => {
    const onDocModal = (e) => setIsDocModalOpen(!!(e && e.detail && e.detail.open));
    window.addEventListener('nombase:document-modal', onDocModal);
    return () => window.removeEventListener('nombase:document-modal', onDocModal);
  }, []);
  const location = useLocation();
  // Page-background uniformity: all primary pages (Network, Plan, Buy,
  // Operations, etc.) share the same neutral background. The previous
  // `/plan` + `/buy` soft-tint was removed to match Network.
  const useTintedBg = false;

  // Use the canonical persona-resolved tenantType from AuthContext.
  // This already accounts for the backend tenant fallback when Clerk
  // metadata is missing/stale (production MVP gating bug fix). Reading
  // `currentUser?.tenant_type` directly here would re-introduce that
  // bug because the raw Clerk value can be null for brand tenants
  // whose Clerk org metadata wasn't populated by the webhook.
  const navItems = (isBrand ? brandNavItems : getSupplierNavItems(tenantType))
    .filter((i) => i.id !== 'demand' || DEMAND_ENABLED)
    .filter((i) => i.id !== 'autopilot' || AUTOPILOT_ENABLED);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Skip link — the first focusable element on the page. Visually
          hidden until focused (Tab), then jumps keyboard / screen-reader
          users straight to the <main id="main"> landmark, bypassing the
          sidebar rail. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
      >
        Skip to main content
      </a>
      {/* Desktop wordmark header removed — the brand mark now lives at the
          top of the FloatingSidebar rail (reference design language). The
          notification bell floats top-right of the page, matching the
          reference header position. */}
      {isBrand && (
        <div className="hidden lg:block fixed top-6 right-8 z-40">
          <NotificationBell />
        </div>
      )}

      {/* Sidebar — auto-collapsed only while the left-anchored document
          modal (PdfSourceModal) is up; otherwise always visible. */}
      {!isDocModalOpen && (
        <FloatingSidebar
          navItems={navItems}
          isBrand={isBrand}
        />
      )}
      
      <div className="flex flex-col min-h-screen">
        <MobileHeader isBrand={isBrand} />
        <main
          id="main"
          tabIndex={-1}
          className={cn(
            "flex-1 p-6 lg:p-10 transition-[margin,padding,background-color] duration-300 focus:outline-none",
            useTintedBg && "bg-slate-50/70",
            "lg:pl-[var(--sidebar-reserve,2.5rem)]"
          )}
        >
          {/* 2026-06-02 — when the AI chat drawer is open, the page
              container fills the remaining viewport (the `mr-[480px]`
              above already reserves room for the drawer). Without
              this, `max-w-6xl mx-auto` would cap the inner content at
              1152px even on a 1440-px-effective workspace, leaving a
              big dead zone between the rightmost table column and the
              drawer's left edge (user-reported on `/network`). When
              the drawer is closed, the historic 6xl cap stays so
              long-form pages don't sprawl on wide monitors. */}
          <div className="mx-auto max-w-6xl">
            {children}
          </div>
        </main>
      </div>

      {/* Operations v2 Phase 4 — universal command bar. ⌘K from anywhere. */}
      <CmdK />
    </div>
  );
}

export default AppShell;
